import { PurchasedProductGuestsPerTier } from "gql/generated";
import { Box, Flex } from "styled-system/jsx";
import { Text } from "@ttc3k/trekker";
import { ConsumingOverlayPageProps, OverlayPage } from "../OverlayPage";
import { GuestNames } from "./GuestNames";
import { GuestCounts } from "./GuestCounts";
import { useBookerContext } from "widgets/booker/contexts";
import { getTotalGuestCount } from "widgets/booker/shared/utils";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

type GuestsPageProps = ConsumingOverlayPageProps & {
  guests: PurchasedProductGuestsPerTier[];
  guestNames: string[];
  onUpdateGuests: (g: PurchasedProductGuestsPerTier) => void;
  onUpdateGuestNames: (name: string, index: number) => void;
  completeGuests: () => void;
  resetGuests: () => void;
};

export const GuestsPage = ({
  guests,
  onUpdateGuests,
  guestNames,
  onUpdateGuestNames,
  completeGuests,
  onClose,
  resetGuests,
  ...rest
}: GuestsPageProps) => {
  const { product } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "GUESTS_PAGE" });

  const guestMetadata = product?.accommodationMetadata?.guestMetadata;
  const isGuestCtAboveMinimum =
    getTotalGuestCount(guests, product?.pricing?.tiers, true) >=
    (guestMetadata?.minimumNumber ?? 0);
  const isGuestCtBelowMaximum = guestMetadata?.maximumNumber
    ? getTotalGuestCount(guests, product?.pricing?.tiers, true) <=
      guestMetadata?.maximumNumber
    : true;

  const isGuestCtValid = isGuestCtAboveMinimum && isGuestCtBelowMaximum;
  const isGuestNamesValid = guestMetadata?.nameEntryRequired
    ? guestNames.every(v => v.length > 0)
    : true;

  const isGuestPageValid = isGuestCtValid && isGuestNamesValid;

  const handleButtonClick = () => {
    completeGuests();
    onClose();
  };

  const handleBackClick = () => {
    resetGuests();
    onClose();
  };
  return (
    <OverlayPage
      title={t("TITLE")}
      subtitle={t("SUBTITLE")}
      buttonDisabled={!isGuestPageValid}
      onButtonClick={handleButtonClick}
      onClose={handleBackClick}
      {...rest}
    >
      <Flex flexDir={"column"} py={"400"} gap={"800"}>
        <Box>
          <GuestCounts
            guests={guests}
            onUpdateGuests={onUpdateGuests}
            priceTiers={product?.pricing?.tiers ?? []}
            guestMaximum={
              product?.accommodationMetadata?.guestMetadata?.maximumNumber
            }
          />
          {(!isGuestCtAboveMinimum || !isGuestCtBelowMaximum) && (
            <Text visual={"smallMedium"} color={"text.warning.mid"}>
              {getGuestCtMessage(
                guestMetadata?.minimumNumber ?? 0,
                guestMetadata?.maximumNumber ?? 0,
                t,
              )}
            </Text>
          )}
        </Box>
        <GuestNames
          guestNames={guestNames}
          onUpdateGuestNames={onUpdateGuestNames}
        />
      </Flex>
    </OverlayPage>
  );
};

function getGuestCtMessage(
  min: number,
  max: number,
  t: TFunction<"booker", undefined>,
) {
  if (min === max) {
    return t("GUEST_CT_MESSAGE.EQUAL", { count: min });
  }
  if (max === 0 && min > 0) {
    return t("GUEST_CT_MESSAGE.MIN", { count: min });
  }
  return t("GUEST_CT_MESSAGE.RANGE", { min, max });
}
